/**
 * ==============================================
 * GIFT NOTIFICATION WIDGET - WEB COMPONENT
 * ==============================================
 *
 * Custom HTML element <gift-notification-widget> that manages gift recipient notification preferences.
 * Supports up to 5 recipients, each with their own notification enable/disable toggle.
 *
 * CORE RESPONSIBILITIES:
 * - Manage multiple recipients (up to 5)
 * - Per-recipient Yes/No toggle for 30-day follow-up notification
 * - Per-recipient applicator color selection
 * - Collect recipient details (name, email, occasion, date)
 * - Validate form data before checkout
 * - Send enabled recipients to Klaviyo for follow-up marketing
 *
 * STATE MANAGEMENT:
 * - No global state object - component is stateless
 * - Each recipient has own notification toggle state stored in DOM
 * - All values read directly from DOM when needed via getRecipientData()
 * - This approach ensures UI and data are always in sync
 *
 * KLAVIYO INTEGRATION:
 * When notification is enabled for a recipient, their data is sent to Klaviyo
 * for a 30-day follow-up email with 15% discount. Different Klaviyo lists are
 * used based on the occasion:
 * - Christmas: List ID "V7BsAy" - "Gift - Christmas 2025"
 * - Father's Day: List ID "TTmmrR" - "Gift - Fathers day 2026"
 * - Birthday: List ID "V4aWhm" - "Gift - Birthday"
 * - Special Occasion: List ID "TmyPcY" - "Gift - Special Occasion"
 *
 * DATA FLOW:
 * 1. User selects "Gift Purchase" in subscription picker
 * 2. This widget becomes visible (controlled by subscription picker)
 * 3. User fills in recipient details (or toggles "No" to skip notification)
 * 4. On checkout, subscription picker calls validateGiftNotification()
 * 5. If valid, getGiftNotificationData() returns all recipient data
 * 6. Data is passed to mps--add-to-cart.js via custom event
 * 7. Cart items get recipient properties for fulfillment tracking
 * 8. (Optionally) sendToKlaviyo() sends enabled recipients to Klaviyo lists
 *
 * VISIBILITY:
 * - Initially hidden (style="display:none" in Liquid)
 * - Made visible by MonthsSubscriptionPicker.updateGiftNotificationVisibility()
 * - Only shown when selectedRefillFrequency === 'gift'
 *
 * RELATED FILES:
 * - months-product-subscription-picker.liquid: Contains the HTML structure
 * - months-product-subscription-picker.js: Controls visibility, triggers validation
 * - mps--add-to-cart.js: Receives recipient data, adds to cart with properties
 */
class GiftNotificationWidget extends HTMLElement {
  constructor() {
    super();

    this.element = this;
    this.MAX_RECIPIENTS = 5;

    /**
     * DOM element references
     */
    this.formContainer = this.element.querySelector('.gift-notification-form');
    this.recipientsContainer = this.element.querySelector('.gift-recipients-container');
    this.addRecipientButton = this.element.querySelector('.gift-add-recipient-btn');

    // Initialize
    this.init();
  }

  /**
   * Initialize component after construction
   * Sets up event listeners and initial UI state
   */
  init() {
    this.bindEvents();
    this.updateAddButtonState();
    this.bindRecipientCardEvents(0); // Bind events for first recipient
    this.updateRecipientFieldsVisibility(0); // Set initial visibility
  }

  /**
   * Bind all event listeners
   */
  bindEvents() {
    // Add recipient button
    if (this.addRecipientButton) {
      this.addRecipientButton.addEventListener('click', () => {
        this.addRecipient();
      });
    }
  }

  /**
   * Bind events for a specific recipient card
   * @param {number} index - Recipient index
   */
  bindRecipientCardEvents(index) {
    const card = this.getRecipientCard(index);
    if (!card) return;

    // Notification toggle (Yes/No buttons)
    const toggleContainer = card.querySelector(`.gift-notification-toggle[data-recipient-index="${index}"]`);
    if (toggleContainer) {
      const toggleButtons = toggleContainer.querySelectorAll('.gift-toggle-btn');
      toggleButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          this.handleRecipientToggleClick(index, btn.dataset.toggle);
        });
      });
    }

    // Occasion change event
    const occasionSelect = card.querySelector('.gift-occasion');
    if (occasionSelect) {
      occasionSelect.addEventListener('change', (e) => {
        this.handleOccasionChange(index, e.target.value);
      });
    }

    // Color picker radio inputs
    const colorInputs = card.querySelectorAll('.gift-color-option input[type="radio"]');
    colorInputs.forEach((input) => {
      input.addEventListener('change', (e) => {
        if (e.target.checked) {
          this.handleRecipientColorSelection(index, e.target.closest('.gift-color-option'));
        }
      });
    });

    // Remove button event
    const removeButton = card.querySelector('.gift-remove-recipient');
    if (removeButton) {
      removeButton.addEventListener('click', () => {
        this.removeRecipient(index);
      });
    }
  }

  /**
   * Handle per-recipient Yes/No toggle button click
   * @param {number} index - Recipient index
   * @param {string} toggleValue - 'yes' or 'no'
   */
  handleRecipientToggleClick(index, toggleValue) {
    const card = this.getRecipientCard(index);
    if (!card) return;

    const toggleContainer = card.querySelector(`.gift-notification-toggle[data-recipient-index="${index}"]`);
    if (!toggleContainer) return;

    // Update button active states
    const toggleButtons = toggleContainer.querySelectorAll('.gift-toggle-btn');
    toggleButtons.forEach((btn) => {
      btn.classList.remove('active');
      if (btn.dataset.toggle === toggleValue) {
        btn.classList.add('active');
      }
    });

    // Show/hide form fields based on selection
    this.updateRecipientFieldsVisibility(index);
  }

  /**
   * Update form fields visibility for a specific recipient
   * @param {number} index - Recipient index
   */
  updateRecipientFieldsVisibility(index) {
    const card = this.getRecipientCard(index);
    if (!card) return;

    const fieldsContainer = card.querySelector(`.gift-recipient-fields[data-recipient-index="${index}"]`);
    const toggleContainer = card.querySelector(`.gift-notification-toggle[data-recipient-index="${index}"]`);

    if (!fieldsContainer || !toggleContainer) return;

    // Check if "Yes" button is active
    const yesButton = toggleContainer.querySelector('[data-toggle="yes"]');
    const isEnabled = yesButton && yesButton.classList.contains('active');

    // Show/hide fields
    fieldsContainer.style.display = isEnabled ? '' : 'none';
  }

  /**
   * Get whether notification is enabled for a specific recipient
   * @param {number} index - Recipient index
   * @returns {boolean}
   */
  isRecipientNotificationEnabled(index) {
    const card = this.getRecipientCard(index);
    if (!card) return false;

    const toggleContainer = card.querySelector(`.gift-notification-toggle[data-recipient-index="${index}"]`);
    if (!toggleContainer) return false;

    const yesButton = toggleContainer.querySelector('[data-toggle="yes"]');
    return yesButton && yesButton.classList.contains('active');
  }

  /**
   * Handle occasion dropdown change
   * @param {number} index - Recipient index
   * @param {string} occasion - Selected occasion value
   */
  handleOccasionChange(index, occasion) {
    // Show/hide date selector based on occasion
    this.updateDateSelectorVisibility(index);

    // Reset date fields when switching occasions
    if (occasion !== 'birthday' && occasion !== 'special-occasion') {
      const card = this.getRecipientCard(index);
      const daySelect = card.querySelector(`[name="gift_day_${index}"]`);
      const monthSelect = card.querySelector(`[name="gift_month_${index}"]`);
      if (daySelect) daySelect.value = '';
      if (monthSelect) monthSelect.value = '';
    }
  }

  /**
   * Update date selector visibility for a specific recipient
   * @param {number} index - Recipient index
   */
  updateDateSelectorVisibility(index) {
    const card = this.getRecipientCard(index);
    if (!card) return;

    const dateSelector = card.querySelector(`.gift-date-selector[data-recipient-index="${index}"]`);
    const occasionSelect = card.querySelector(`[name="gift_occasion_${index}"]`);

    if (dateSelector && occasionSelect) {
      const occasion = occasionSelect.value || 'christmas';
      const showDateSelector = occasion === 'birthday' || occasion === 'special-occasion';
      dateSelector.style.display = showDateSelector ? '' : 'none';
    }
  }

  /**
   * Handle color selection for a specific recipient
   * Updates visual state to match main color picker behavior
   * @param {number} index - Recipient index
   * @param {HTMLElement} option - Selected color option element
   */
  handleRecipientColorSelection(index, option) {
    const card = this.getRecipientCard(index);
    if (!card) return;

    const selectedColor = option.dataset.color;

    // Remove selected class from all color options for this recipient
    const allColorOptions = card.querySelectorAll('.gift-color-option');
    allColorOptions.forEach((opt) => opt.classList.remove('gift-color-option--selected'));

    // Add selected class to chosen option
    option.classList.add('gift-color-option--selected');

    // Ensure radio input is checked
    const radioInput = option.querySelector('input[type="radio"]');
    if (radioInput) {
      radioInput.checked = true;
    }
  }

  /**
   * Get recipient card element by index
   * @param {number} index - Recipient index
   * @returns {HTMLElement|null}
   */
  getRecipientCard(index) {
    return this.recipientsContainer.querySelector(`.gift-recipient-card[data-recipient-index="${index}"]`);
  }

  /**
   * Get current number of recipients
   * @returns {number}
   */
  getRecipientCount() {
    return this.recipientsContainer.querySelectorAll('.gift-recipient-card').length;
  }

  /**
   * Get recipient label text by index
   * @param {number} index - Recipient index (0-based)
   * @returns {string}
   */
  getRecipientLabel(index) {
    const labels = ['First Recipient:', 'Second Recipient:', 'Third Recipient:', 'Fourth Recipient:', 'Fifth Recipient:'];
    return labels[index] || `Recipient ${index + 1}:`;
  }

  /**
   * Add a new recipient card
   */
  addRecipient() {
    const currentCount = this.getRecipientCount();

    if (currentCount >= this.MAX_RECIPIENTS) {
      console.warn('Maximum recipients reached');
      return;
    }

    const newIndex = currentCount;
    const newCard = this.createRecipientCard(newIndex);

    this.recipientsContainer.appendChild(newCard);
    this.bindRecipientCardEvents(newIndex);
    this.updateRecipientFieldsVisibility(newIndex);
    this.updateRemoveButtonsVisibility();
    this.updateAddButtonState();
    this.dispatchRecipientsChangedEvent();
  }

  /**
   * Create a new recipient card element
   * @param {number} index - Recipient index
   * @returns {HTMLElement}
   */
  createRecipientCard(index) {
    const card = document.createElement('div');
    card.className = 'gift-recipient-card';
    card.setAttribute('data-recipient-index', index);

    card.innerHTML = `
      <div class="gift-recipient-header">
        <span class="gift-recipient-label">${this.getRecipientLabel(index)}</span>
        <button type="button" class="gift-remove-recipient" data-recipient-index="${index}">
          Remove
        </button>
      </div>

      <div class="gift-recipient-notification-section">
        <p class="gift-notification-question">
          Notify this recipient in 30 days after gifting date e.g Christmas Day, Birthday (select date) for 15% off their next refill?
          <span class="optional-text">(Optional)</span>
        </p>
        <div class="gift-notification-toggle" data-recipient-index="${index}">
          <button
            type="button"
            class="gift-toggle-btn gift-toggle-btn--yes active"
            data-toggle="yes"
            data-recipient-index="${index}"
          >
            Yes
          </button>
          <button
            type="button"
            class="gift-toggle-btn gift-toggle-btn--no"
            data-toggle="no"
            data-recipient-index="${index}"
          >
            No
          </button>
        </div>
      </div>

      ${this.getColorDropdownHTML(index)}

      <div class="gift-recipient-fields" data-recipient-index="${index}">
        <p>Scent: The Evening Man (Sandalwood & Geranium)</p>
        <div class="gift-form-field">
          <select class="gift-input gift-select gift-occasion" name="gift_occasion_${index}" data-recipient-index="${index}">
            <option value="special-occasion">Special Occasion</option>  
            <option value="valentines-day">Valentines Day</option>
            <option value="fathers-day">Fathers Day</option>
            <option value="birthday">Birthday</option>
            
          </select>
          <svg class="gift-select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>

        <div class="gift-date-selector" data-recipient-index="${index}">
          <div class="gift-date-fields">
            <div class="gift-form-field gift-form-field--small">
              <select class="gift-input gift-select" name="gift_day_${index}" data-recipient-index="${index}">
                <option value="">Day</option>
                ${Array.from({ length: 31 }, (_, i) => `<option value="${i + 1}">${i + 1}</option>`).join('')}
              </select>
              <svg class="gift-select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="gift-form-field gift-form-field--small">
              <select class="gift-input gift-select" name="gift_month_${index}" data-recipient-index="${index}">
                <option value="">Month</option>
                <option value="1">January</option>
                <option value="2">February</option>
                <option value="3">March</option>
                <option value="4">April</option>
                <option value="5">May</option>
                <option value="6">June</option>
                <option value="7">July</option>
                <option value="8">August</option>
                <option value="9">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>
              <svg class="gift-select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="gift-form-field">
          <input
            type="text"
            class="gift-input"
            name="gift_recipient_first_name_${index}"
            data-recipient-index="${index}"
            placeholder="Recipient name"
          >
        </div>

        <div class="gift-form-field">
          <input
            type="email"
            class="gift-input"
            name="gift_recipient_email_${index}"
            data-recipient-index="${index}"
            placeholder="Recipient email"
          >
        </div>
      </div>
    `;

    return card;
  }

  /**
   * Get color picker HTML for a recipient card (VISUAL PICKER VERSION)
   * @param {number} index - Recipient index
   * @returns {string} HTML string for visual color picker
   */
  getColorDropdownHTML(index) {
    const colorOptions = this.getColorOptions();
    if (!colorOptions || colorOptions.length === 0) {
      return ''; // No color options available
    }

    // Get color variant data with images
    const colorVariantData = this.getColorVariantData();

    const optionsHTML = colorOptions
      .map((color, i) => {
        // Find matching variant for this color
        const variantData = colorVariantData.find((v) => v.color === color);
        const imageUrl = variantData?.imageUrl || '';
        const isSoldOut = color?.variant?.metafields?.custom?.is_refill_sold_out?.value == true || color?.variant?.available == false;
        return `
          <label class="gift-color-option ${i === 0 ? 'gift-color-option--selected' : ''}"
                 data-color="${color}"
                 data-recipient-index="${index}">
            <input
              type="radio"
              name="gift_recipient_color_${index}"
              value="${color}"
              ${i === 0 ? 'checked' : ''}
              ${isSoldOut ? 'disabled="disabled"' : ''}
              class="gift-color-option__input"
            >
            <div class="gift-color-option__card">
              <div class="gift-color-option__image-container">
                ${
                  imageUrl
                    ? `
                  <img
                    src="${imageUrl}"
                    alt="${color}"
                    class="gift-color-option__image"
                    loading="lazy"
                    width="300"
                    height="300"
                  >
                `
                    : ''
                }
              </div>
              <div class="gift-color-option__label">${color}</div>
            </div>
          </label>
        `;
      })
      .join('');

    return `
      <div class="gift-recipient-color-section">
        <label class="gift-color-label">Choose applicator colour:</label>
        <div class="gift-color-picker-grid">
          ${optionsHTML}
        </div>
      </div>
    `;
  }

  /**
   * Get available color options from subscription picker config
   * @returns {Array<string>} Array of color values
   */
  getColorOptions() {
    try {
      const configData = JSON.parse(document.getElementById('months-product-subscription-picker-data')?.textContent || '{}');
      return configData.color_option_values || [];
    } catch (error) {
      console.error('Error reading color options:', error);
      return [];
    }
  }

  /**
   * Get first color option as default
   * @returns {string} First color value or empty string
   */
  getFirstColorOption() {
    const colors = this.getColorOptions();
    return colors.length > 0 ? colors[0] : '';
  }

  /**
   * Get color variant data including images
   * @returns {Array<Object>} Array of color variant objects with images
   */
  getColorVariantData() {
    try {
      const configData = JSON.parse(document.getElementById('months-product-subscription-picker-data')?.textContent || '{}');

      const colorValues = configData.color_option_values || [];

      if (!colorValues.length) return [];

      return colorValues.map((colorValue) => ({
        color: colorValue,
        imageUrl: this.getColorVariantImage(colorValue),
      }));
    } catch (error) {
      console.error('Error reading color variant data:', error);
      return [];
    }
  }

  /**
   * Get image URL for a specific color variant
   * @param {string} colorValue - Color value to find image for
   * @returns {string} Image URL or empty string
   */
  getColorVariantImage(colorValue) {
    try {
      const configData = JSON.parse(document.getElementById('months-product-subscription-picker-data')?.textContent || '{}');

      const variants = configData.shower_shower_pack_product_varaints || [];
      const colorOption = configData.color_option;

      if (!colorOption || !variants.length) return '';

      const optionPosition = colorOption.position;
      const optionKey = `option${optionPosition}`;

      // Find variant matching this color
      const variant = variants.find((v) => v[optionKey] === colorValue);

      // Return featured media image URL
      if (variant?.featured_image?.src) {
        return variant.featured_image.src;
      }

      return '';
    } catch (error) {
      console.error('Error getting color variant image:', error);
      return '';
    }
  }

  /**
   * Remove a recipient card
   * @param {number} index - Recipient index to remove
   */
  removeRecipient(index) {
    const card = this.getRecipientCard(index);
    if (!card) return;

    // Don't allow removing if only one recipient remains
    if (this.getRecipientCount() <= 1) {
      console.warn('Cannot remove last recipient');
      return;
    }

    card.remove();
    this.reindexRecipients();
    this.updateRemoveButtonsVisibility();
    this.updateAddButtonState();
    this.dispatchRecipientsChangedEvent();
  }

  /**
   * Reindex all recipient cards after removal
   * Updates data attributes and field names to maintain sequential indexing
   */
  reindexRecipients() {
    const cards = this.recipientsContainer.querySelectorAll('.gift-recipient-card');

    cards.forEach((card, newIndex) => {
      // Update card data attribute
      card.setAttribute('data-recipient-index', newIndex);

      // Update label
      const label = card.querySelector('.gift-recipient-label');
      if (label) {
        label.textContent = this.getRecipientLabel(newIndex);
      }

      // Update all field names and data attributes within the card
      const fields = card.querySelectorAll('[data-recipient-index]');
      fields.forEach((field) => {
        field.setAttribute('data-recipient-index', newIndex);

        // Update name attribute
        if (field.hasAttribute('name')) {
          const nameBase = field.getAttribute('name').replace(/_\d+$/, '');
          field.setAttribute('name', `${nameBase}_${newIndex}`);
        }
      });
    });
  }

  /**
   * Update visibility of remove buttons
   * Hide remove buttons when only 1 recipient, show otherwise
   */
  updateRemoveButtonsVisibility() {
    const removeButtons = this.recipientsContainer.querySelectorAll('.gift-remove-recipient');
    const showButtons = this.getRecipientCount() > 1;

    removeButtons.forEach((btn) => {
      btn.style.display = showButtons ? '' : 'none';
    });
  }

  /**
   * Update add button state (disable at max recipients)
   */
  updateAddButtonState() {
    if (!this.addRecipientButton) return;

    const currentCount = this.getRecipientCount();
    this.addRecipientButton.disabled = currentCount >= this.MAX_RECIPIENTS;

    if (currentCount >= this.MAX_RECIPIENTS) {
      this.addRecipientButton.textContent = `Maximum ${this.MAX_RECIPIENTS} recipients`;
    } else {
      this.addRecipientButton.textContent = 'Add another Gift recipient?';
    }
  }

  /**
   * Dispatch event when recipient count changes
   */
  dispatchRecipientsChangedEvent() {
    const event = new CustomEvent('gift-recipients-changed', {
      detail: { recipientCount: this.getRecipientCount() },
      bubbles: true,
    });
    this.dispatchEvent(event);
  }

  /**
   * Get recipient data from a specific card
   * @param {number} index - Recipient index
   * @returns {Object|null}
   */
  getRecipientData(index) {
    const card = this.getRecipientCard(index);
    if (!card) return null;

    // Check if notification is enabled for this recipient
    const notificationEnabled = this.isRecipientNotificationEnabled(index);

    const occasionSelect = card.querySelector(`[name="gift_occasion_${index}"]`);
    const colorInput = card.querySelector(`[name="gift_recipient_color_${index}"]:checked`);
    const firstNameInput = card.querySelector(`[name="gift_recipient_first_name_${index}"]`);
    const emailInput = card.querySelector(`[name="gift_recipient_email_${index}"]`);
    const daySelect = card.querySelector(`[name="gift_day_${index}"]`);
    const monthSelect = card.querySelector(`[name="gift_month_${index}"]`);

    const occasion = occasionSelect?.value || 'christmas';
    const recipientColor = colorInput?.value || this.getFirstColorOption();
    const recipientFirstName = (firstNameInput?.value || '').trim();
    const recipientEmail = (emailInput?.value || '').trim();

    const data = {
      notificationEnabled,
      recipientColor,
      recipientFirstName,
      recipientEmail,
      occasion,
      klaviyoListId: this.getKlaviyoListId(occasion),
      klaviyoListName: this.getKlaviyoListName(occasion),
    };

    // Add date fields for birthday/special occasion
    if (occasion === 'birthday' || occasion === 'special-occasion') {
      data.day = daySelect?.value || '';
      data.month = monthSelect?.value || '';
    }

    return data;
  }

  /**
   * Get current gift notification data for all recipients
   * Reads directly from DOM
   * @returns {Object} Gift notification data
   */
  getGiftNotificationData() {
    const recipientCount = this.getRecipientCount();
    const recipients = [];

    for (let i = 0; i < recipientCount; i++) {
      const recipientData = this.getRecipientData(i);
      if (recipientData) {
        recipients.push(recipientData);
      }
    }

    return {
      recipients: recipients,
    };
  }

  /**
   * Validate gift notification data for enabled recipients only
   * @returns {Object} Validation result with isValid and errors array
   */
  validateGiftNotification() {
    const errors = [];

    // Hide any previous errors
    this.hideErrors();

    const recipientCount = this.getRecipientCount();

    // Validate each recipient where notification is enabled
    for (let i = 0; i < recipientCount; i++) {
      const recipientData = this.getRecipientData(i);
      if (!recipientData) continue;

      // Skip validation if notification is not enabled for this recipient
      if (!recipientData.notificationEnabled) {
        continue;
      }

      const label = this.getRecipientLabel(i).replace(':', '');

      // Validate required fields
      if (!recipientData.recipientFirstName) {
        errors.push(`${label}: Name is required`);
      }

      if (!recipientData.recipientEmail) {
        errors.push(`${label}: Email is required`);
      } else {
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(recipientData.recipientEmail)) {
          errors.push(`${label}: Please enter a valid email address`);
        }
      }

      // Validate date fields for birthday/special occasion
      if (recipientData.occasion === 'birthday' || recipientData.occasion === 'special-occasion') {
        if (!recipientData.day) errors.push(`${label}: Day is required`);
        if (!recipientData.month) errors.push(`${label}: Month is required`);
      }
    }

    // Show errors if any
    if (errors.length > 0) {
      this.showErrors(errors);
    }

    return {
      isValid: errors.length === 0,
      errors: errors,
    };
  }

  /**
   * Reset widget to default state (1 empty recipient with notification enabled)
   */
  reset() {
    // Remove all recipient cards except the first
    const cards = this.recipientsContainer.querySelectorAll('.gift-recipient-card');
    cards.forEach((card, index) => {
      if (index > 0) {
        card.remove();
      }
    });

    // Reset first recipient
    const firstCard = this.getRecipientCard(0);
    if (firstCard) {
      // Reset toggle to "Yes"
      const toggleContainer = firstCard.querySelector('.gift-notification-toggle[data-recipient-index="0"]');
      if (toggleContainer) {
        const buttons = toggleContainer.querySelectorAll('.gift-toggle-btn');
        buttons.forEach((btn) => {
          btn.classList.remove('active');
          if (btn.dataset.toggle === 'yes') {
            btn.classList.add('active');
          }
        });
      }

      // Reset form fields
      const occasionSelect = firstCard.querySelector('[name="gift_occasion_0"]');
      const firstNameInput = firstCard.querySelector('[name="gift_recipient_first_name_0"]');
      const emailInput = firstCard.querySelector('[name="gift_recipient_email_0"]');
      const daySelect = firstCard.querySelector('[name="gift_day_0"]');
      const monthSelect = firstCard.querySelector('[name="gift_month_0"]');

      if (occasionSelect) occasionSelect.value = 'christmas';
      if (firstNameInput) firstNameInput.value = '';
      if (emailInput) emailInput.value = '';
      if (daySelect) daySelect.value = '';
      if (monthSelect) monthSelect.value = '';

      // Reset color selection to first option (visual picker)
      const colorOptions = firstCard.querySelectorAll('.gift-color-option');
      const colorInputs = firstCard.querySelectorAll('.gift-color-option input[type="radio"]');

      colorOptions.forEach((opt, i) => {
        if (i === 0) {
          opt.classList.add('gift-color-option--selected');
        } else {
          opt.classList.remove('gift-color-option--selected');
        }
      });

      if (colorInputs.length > 0) {
        colorInputs[0].checked = true;
      }
    }

    // Update visibility
    this.updateRecipientFieldsVisibility(0);
    this.updateDateSelectorVisibility(0);
    this.updateRemoveButtonsVisibility();
    this.updateAddButtonState();
    this.hideErrors();
  }

  /**
   * Show validation errors in the form
   * @param {Array<string>} errors - Array of error messages to display
   */
  showErrors(errors) {
    const errorContainer = this.element.querySelector('.gift-error-message');
    const errorList = this.element.querySelector('.gift-error-list');

    if (!errorContainer || !errorList || !errors || errors.length === 0) return;

    // Clear existing errors
    errorList.innerHTML = '';

    // Add each error as a list item
    errors.forEach((error) => {
      const li = document.createElement('li');
      li.textContent = error;
      errorList.appendChild(li);
    });

    // Show error container
    errorContainer.style.display = '';
  }

  /**
   * Hide error messages
   */
  hideErrors() {
    const errorContainer = this.element.querySelector('.gift-error-message');
    if (errorContainer) {
      errorContainer.style.display = 'none';
    }
  }

  /**
   * Get Klaviyo list ID based on occasion
   *
   * KLAVIYO LIST CONFIGURATION:
   * These list IDs correspond to lists created in the Klaviyo dashboard.
   * Recipients are added to occasion-specific lists so they receive
   * appropriately timed and themed follow-up emails.
   *
   * LIST IDs (created in Klaviyo admin):
   * - Christmas: "V7BsAy" - Triggered around Dec 25
   * - Father's Day: "TTmmrR" - Triggered around Father's Day
   * - Birthday: "V4aWhm" - Triggered on specific date entered by customer
   * - Special Occasion: "TmyPcY" - Triggered on specific date entered
   *
   * IMPORTANT: If these lists are modified or deleted in Klaviyo,
   * update the IDs here to match the new list IDs.
   *
   * @param {string} occasion - Occasion value from dropdown ('christmas', 'fathers-day', 'birthday', 'special-occasion')
   * @returns {string} Klaviyo list ID (6-character alphanumeric string)
   */
  getKlaviyoListId(occasion) {
    const listIdMap = {
      christmas: 'V7BsAy',
      'fathers-day': 'TTmmrR',
      birthday: 'V4aWhm',
      'special-occasion': 'TmyPcY',
    };

    return listIdMap[occasion] || listIdMap['christmas'];
  }

  /**
   * Get Klaviyo list name based on occasion
   *
   * Human-readable names stored as properties on the Klaviyo profile.
   * Useful for segmentation and reporting in Klaviyo dashboard.
   *
   * NOTE: Year suffix in names (e.g., "Christmas 2025") should be updated
   * annually or made dynamic based on current date.
   *
   * @param {string} occasion - Occasion value from dropdown
   * @returns {string} Klaviyo list name (human-readable)
   */
  getKlaviyoListName(occasion) {
    const occasionMap = {
      christmas: 'Gift - Christmas 2025',
      'fathers-day': 'Gift - Fathers day 2026',
      birthday: 'Gift - Birthday',
      'special-occasion': 'Gift - Special Occasion',
    };

    return occasionMap[occasion] || 'Gift - General';
  }

  /**
   * Send enabled gift recipients to Klaviyo
   * Only sends recipients where notification is enabled (Yes toggle active)
   *
   * FLOW:
   * 1. Validate all recipient data first
   * 2. Filter to only recipients with notificationEnabled = true
   * 3. For each enabled recipient, call sendRecipientToKlaviyo()
   * 4. Log success/failure for each recipient
   *
   * ERROR HANDLING:
   * Errors for individual recipients are logged but don't block other recipients.
   * This ensures one invalid email doesn't prevent other recipients from being added.
   *
   * TIMING:
   * This is typically called after cart is successfully created in mps--add-to-cart.js.
   * Currently commented out in mps--add-to-cart.js - can be enabled when needed.
   */
  async sendToKlaviyo() {
    // Validate data before sending
    const validation = this.validateGiftNotification();
    if (!validation.isValid) {
      console.warn('Cannot send to Klaviyo: Invalid gift notification data', validation.errors);
      return;
    }

    /**
     * KLAVIYO COMPANY ID
     * This is Bob's unique Klaviyo account identifier.
     * Found in Klaviyo dashboard: Settings > API Keys > Public API Key (site ID)
     *
     * IMPORTANT: This is the PUBLIC key, safe to use in client-side code.
     * It only allows subscribing profiles, not accessing account data.
     */
    const klaviyoCompanyId = 'XVB4x9';
    const data = this.getGiftNotificationData();

    if (!data || !data.recipients || data.recipients.length === 0) {
      console.warn('No recipients to send to Klaviyo');
      return;
    }

    // Filter to only enabled recipients
    const enabledRecipients = data.recipients.filter((r) => r.notificationEnabled);

    if (enabledRecipients.length === 0) {
      console.log('No recipients have notification enabled, skipping Klaviyo');
      return;
    }

    console.log(`Sending ${enabledRecipients.length} enabled recipient(s) to Klaviyo`);

    // Send each enabled recipient to Klaviyo
    for (const recipient of enabledRecipients) {
      try {
        await this.sendRecipientToKlaviyo(klaviyoCompanyId, recipient);
        console.log(`Successfully sent recipient ${recipient.recipientEmail} to Klaviyo list: ${recipient.klaviyoListName}`);
      } catch (error) {
        console.error(`Failed to send recipient ${recipient.recipientEmail} to Klaviyo:`, error);
      }
    }
  }

  /**
   * Send a single recipient to Klaviyo
   * @param {string} companyId - Klaviyo company ID
   * @param {Object} recipient - Recipient data
   */
  async sendRecipientToKlaviyo(companyId, recipient) {
    const properties = {};

    // Add gift notification metadata
    properties['gift_notification'] = 'true';
    properties['gift_notification_at'] = new Date().toISOString();
    properties['gift_occasion'] = recipient.occasion;
    properties['gift_occasion_name'] = recipient.klaviyoListName;

    // Add date for birthday/special occasion
    if (recipient.occasion === 'birthday' || recipient.occasion === 'special-occasion') {
      if (recipient.day && recipient.month) {
        properties['gift_day'] = recipient.day;
        properties['gift_month'] = recipient.month;
      }
    }

    // Prepare the profile data structure for Klaviyo API
    const profileData = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                properties,
                subscriptions: {
                  email: {
                    marketing: {
                      consent: 'SUBSCRIBED',
                      consented_at: new Date().toISOString(),
                    },
                  },
                },
                email: recipient.recipientEmail,
                first_name: recipient.recipientFirstName,
              },
            },
          },
          custom_source: 'Gift Purchase - 30 Day Follow-up',
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: recipient.klaviyoListId,
            },
          },
        },
      },
    };

    // Send to Klaviyo
    const url = `https://a.klaviyo.com/client/subscriptions?company_id=${companyId}`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/vnd.api+json',
          'Content-Type': 'application/vnd.api+json',
          Revision: '2025-07-15',
        },
        body: JSON.stringify(profileData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Klaviyo API error (${response.status}): ${errorText}`);
      }

      return response.ok;
    } catch (error) {
      console.error('Klaviyo profile creation failed:', error);
      throw error;
    }
  }
}

// Register custom element
customElements.define('gift-notification-widget', GiftNotificationWidget);
